/*
 * Copyright (c) 2018-2023 MemberCenter, a subsidiary of Cameron Collective
 *   All Rights Reserved
 */
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { Editor } from '@tinymce/tinymce-react';
import { useEffect } from 'react';

import CarbonCopyInput from 'components/CarbonCopyInput';
import MEMBER_EMAILS_CONFIG from 'config/member_emails.config';
import { RENEWAL_ONE_TYPE } from 'routes/onboard/member_email/constants';

import { help_tabs, plugins, toolbar } from './config';
import { styles } from './styles';

const DEFAULT_FREQUENCY = 90;

const MemberEmailNotificationForm = ({
	handleChange,
	frequency,
	email_type,
	email_subject,
	email_body,
	memberemailformcc_set = [],
	id
}) => {
	const onValueChange = prop => event => {
		handleChange(prop, event.target.value);
	};

	const onEmailBodyChange = content => {
		handleChange('email_body', content);
	};

	const showFrequencyField = email_type === RENEWAL_ONE_TYPE;

	useEffect(() => {
		if (!showFrequencyField) {
			handleChange('frequency', DEFAULT_FREQUENCY);
		}
	}, [handleChange, showFrequencyField]);

	const onCarbonCopyChange = (event, values) => {
		const formattedValue = values.map(value => {
			if (typeof value === 'string') {
				return {
					member_email_form: id,
					email: value
				};
			}

			return value;
		});

		handleChange('memberemailformcc_set', formattedValue);
	};

	return (
		<Box display="flex" flexDirection="column">
			<InputLabel htmlFor="renewal-simple">Email Type</InputLabel>
			<Select
				variant="filled"
				value={email_type}
				onChange={onValueChange('email_type')}
				inputProps={{
					name: 'renewal_type',
					id: 'renewal-simple'
				}}
			>
				{MEMBER_EMAILS_CONFIG.EMAIL_TYPE.map(option => (
					<MenuItem key={option.db_key} value={option.db_key}>
						{option.display}
					</MenuItem>
				))}
			</Select>
			{showFrequencyField && (
				<>
					<Box sx={styles.bottomOffset} />
					<InputLabel htmlFor="frequency">Frequency</InputLabel>
					<Select
						variant="filled"
						value={frequency}
						onChange={onValueChange('frequency')}
						required
						inputProps={{
							name: 'frequency',
							id: 'frequency'
						}}
					>
						{MEMBER_EMAILS_CONFIG.EMAIL_FREQUENCY.map(option => (
							<MenuItem key={option.value} value={option.value}>
								{option.display}
							</MenuItem>
						))}
					</Select>
				</>
			)}
			<Divider sx={styles.divider} />
			<CarbonCopyInput
				textFieldProps={{
					label: 'CC (Carbon Copy)',
					variant: 'standard'
				}}
				value={memberemailformcc_set}
				options={memberemailformcc_set}
				onChange={onCarbonCopyChange}
			/>
			<Box sx={styles.bottomOffset} />
			<TextField
				autoComplete="email_subject"
				name="email_subject"
				value={email_subject}
				onChange={onValueChange('email_subject')}
				required
				fullWidth
				id="email_subject"
				label="Email Subject"
				variant="standard"
			/>
			<Box sx={styles.bottomOffset} />
			<Editor
				value={email_body}
				onEditorChange={onEmailBodyChange}
				apiKey={process.env.REACT_APP_TINY_KEY}
				init={{
					height: 400,
					menubar: false,
					plugins,
					toolbar,
					content_style: 'body { font-family: Open Sans,Helvetica,Arial,sans-serif; font-size:14px }',
					help_tabs
				}}
			/>
		</Box>
	);
};

export default MemberEmailNotificationForm;
